/**
 * reportToSource — extracts normalized business facts from a Report into the backend
 * generator's SourceInput shape. This is request-shaping only: the frontend does NOT
 * plan, generate, or store a schema. The Go backend receives these facts and owns the
 * entire pipeline (BCM → BIM → plan → validate → repair → persist). Reuses the proven
 * report→content extraction so no derivation logic is duplicated.
 */
import type { ReportPayload } from '../../types'
import type { WebsiteLocale } from '../schema'
import type { SiteBuilderSource } from '../../api/client'
import { toBusinessContent } from './reportContentMapper'

export function reportToSource(report: ReportPayload, locale: WebsiteLocale): SiteBuilderSource {
  const bcm = toBusinessContent(report, locale)
  return {
    name: bcm.identity.name,
    category: bcm.identity.category || undefined,
    categories: bcm.identity.categories.length > 0 ? [...bcm.identity.categories] : undefined,
    tagline: bcm.identity.tagline,
    shortDescription: bcm.identity.shortDescription,
    description: bcm.identity.description,
    offerings: bcm.offerings.map((o) => ({ name: o.name, description: o.description })),
    contact: { phone: bcm.contact.phone, email: bcm.contact.email, website: bcm.contact.website },
    address: bcm.location.formattedAddress,
    brandColors: [...bcm.brand.brandColors],
    socialLinks: bcm.brand.socialLinks.map((s) => ({ platform: s.platform, url: s.url })),
    reviews: bcm.reviews.highlights.map((h) => ({ author: h.author, rating: h.rating, text: h.text })),
    averageRating: bcm.reviews.averageRating,
    reviewCount: bcm.reviews.reviewCount,
    media: {
      logoUrl: bcm.brand.logo?.url,
      coverUrl: bcm.media.cover[0]?.url,
      galleryUrls: bcm.media.gallery.map((a) => a.url),
    },
    attributes: [...bcm.attributes],
    priceLevel: bcm.priceLevel,
    seo: { keywords: bcm.seo.keywords ? [...bcm.seo.keywords] : undefined },
  }
}
